import {
  ChangeEvent,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { UserContext } from 'globalContext/UserContext';
import moment from 'moment';
import {
  fetchBulkOrderConsultants,
  fetchOrderReferenceData,
  issueBulkOrders,
  issueBulkOrdersCsv,
  previewBulkOrders,
  previewBulkOrdersCsv,
} from './api';
import DateField from './DateField';
import OrderAccess from './OrderAccess';
import OrderCodeField from './OrderCodeField';
import OrderSubjectField from './OrderSubjectField';
import PageHeader from './PageHeader';
import SearchableSelect from './SearchableSelect';
import {
  BulkOrderConsultant,
  BulkOrderPreview,
  BulkOrderResult,
  BulkOrderRowValues,
  BulkOrderSharedFields,
  SelectOption,
} from './types';
import {
  addBulkConsultants,
  BulkConsultantRow,
  bulkErrorsCsv,
  consultantName,
  followStartDate,
  getDeadlineMax,
  getDefaultIssueDates,
  getEarliestStartDate,
  isInvalidDate,
  optionalText,
  ORDER_CONDITIONS_TEMPLATE,
  toBulkOrderRows,
  toOrderCode,
  validateBulkConditions,
  validateOrderCode,
  withRowStartDate,
} from './utils';
import * as Styles from './Orders.css';

type InputMode = 'list' | 'csv';
type BulkError =
  | 'validation'
  | 'dates'
  | 'forbidden'
  | 'request'
  | 'consultants'
  | 'conditions';

const DATE_LABELS = {
  startDate: 'ORDERS.START_DATE',
  endDate: 'ORDERS.END_DATE',
  decisionDeadline: 'ORDERS.DEADLINE',
};

const DATE_KEYS = ['startDate', 'endDate', 'decisionDeadline'] as const;

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

const bulkErrorMessage = (error: BulkError, conditionsError: string) => {
  if (error === 'conditions') {
    return conditionsError;
  }
  if (error === 'validation') {
    return 'ORDERS.BULK.VALIDATION.REQUIRED';
  }
  if (error === 'forbidden') {
    return 'ORDERS.ACTION_ERROR.UNAUTHORIZED';
  }
  if (error === 'dates') {
    return 'ORDERS.VALIDATION.DATES';
  }
  if (error === 'consultants') {
    return 'ORDERS.BULK.CONSULTANTS_ERROR';
  }
  return 'ORDERS.BULK.ERROR';
};

// The line under an empty batch says whom "Add all" adds, or why it cannot.
const getEmptyBatchMessage = (
  clientSelected: boolean,
  addAllCount: number | null,
) => {
  if (!clientSelected) {
    return 'ORDERS.BULK.SELECT_CLIENT_FIRST';
  }
  if (addAllCount === 0) {
    return 'ORDERS.BULK.CONSULTANTS_EMPTY_NOBODY';
  }
  return addAllCount
    ? 'ORDERS.BULK.CONSULTANTS_EMPTY_COUNT'
    : 'ORDERS.BULK.CONSULTANTS_EMPTY';
};

interface FormState {
  clientId: string;
  ownerId: string;
  startDate: string;
  endDate: string;
  decisionDeadline: string;
  subject: string;
  conditions: string;
  orderCode: string;
}

const EMPTY_FORM: FormState = {
  clientId: '',
  ownerId: '',
  startDate: '',
  endDate: '',
  decisionDeadline: '',
  subject: '',
  conditions: '',
  orderCode: '',
};

const initialForm = (): FormState => {
  const { startDate, decisionDeadline } = getDefaultIssueDates();
  return {
    ...EMPTY_FORM,
    startDate: startDate.format('YYYY-MM-DD'),
    decisionDeadline: decisionDeadline.format('YYYY-MM-DD'),
    // The shared conditions open with the template (P3).
    conditions: ORDER_CONDITIONS_TEMPLATE,
  };
};

// Go-live readiness links here with ?clientId=1&consultantIds=2,3 to issue the
// missing orders in one batch.
const readPrefill = (search: string) => {
  const params = new URLSearchParams(search);
  const clientId = params.get('clientId') || '';
  const consultantIds = (params.get('consultantIds') || '')
    .split(',')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  return { clientId, consultantIds };
};

const toRow = ({
  accountId,
  fullName,
  email,
  contractType,
  awaitingFirstSignIn,
}: BulkOrderConsultant): BulkConsultantRow => ({
  accountId,
  fullName,
  email,
  contractType,
  awaitingFirstSignIn,
});

const saveTextFile = (content: string, filename: string) => {
  const url = URL.createObjectURL(
    new Blob([content], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const isBulkOrderResult = (value: unknown): value is BulkOrderResult => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const result = value as Partial<BulkOrderResult>;
  return (
    typeof result.created === 'boolean' &&
    Array.isArray(result.orders) &&
    Array.isArray(result.errors)
  );
};

export const BulkOrderIssueContent = () => {
  const intl = useIntl();
  // Change request of 30.10: starts may fall in the month not yet closed.
  const earliestStart = getEarliestStartDate().format('YYYY-MM-DD');
  const location = useLocation();
  const { username } = useContext(UserContext);
  const [mode, setMode] = useState<InputMode>('list');
  const [consultants, setConsultants] = useState<BulkConsultantRow[]>([]);
  const [pickerValue, setPickerValue] = useState<SelectOption | null>(null);
  // Cleared after every pick so the next search starts empty.
  const [pickerInput, setPickerInput] = useState('');
  const [notifyAlso, setNotifyAlso] = useState<SelectOption[]>([]);
  const [editedRow, setEditedRow] = useState<number | null>(null);
  const [loadingConsultants, setLoadingConsultants] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [clientConsultants, setClientConsultants] = useState<
    BulkOrderConsultant[] | null
  >(null);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  // The consultant picker lists active accounts only (B7), without
  // employment-contract employees (change request of 26.10, P2).
  const [activeEmployees, setActiveEmployees] = useState<SelectOption[]>([]);
  // Notify also: only people who manage or view all assignments (D2).
  const [notifyCandidates, setNotifyCandidates] = useState<SelectOption[]>([]);
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [referenceState, setReferenceState] = useState<
    'loading' | 'ready' | 'partial' | 'error'
  >('loading');
  const [preview, setPreview] = useState<BulkOrderPreview | null>(null);
  const [result, setResult] = useState<BulkOrderResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<BulkError | null>(null);
  const [errorDetail, setErrorDetail] = useState('');
  // The message key of a conditions error, shown while error is 'conditions'.
  const [conditionsError, setConditionsError] = useState('');
  const requestRevision = useRef(0);
  const clientRevision = useRef(0);
  const clientSelected = Boolean(form.clientId);
  const clientName =
    clients.find(({ value }) => String(value) === form.clientId)?.name || '';
  // How many people "Add all" would add, once the client's consultants are
  // known (change request of 07.10, variant A).
  const addAllCount = clientConsultants
    ? clientConsultants.filter(
        ({ accountId }) =>
          !consultants.some((row) => row.accountId === accountId),
      ).length
    : null;

  useEffect(() => {
    let mounted = true;
    fetchOrderReferenceData({
      activeConsultants: true,
      notifyCandidates: true,
    }).then((references) => {
      if (!mounted) {
        return;
      }
      setEmployees(references.employees);
      setActiveEmployees(references.activeEmployees);
      setNotifyCandidates(references.notifyCandidates);
      setClients(references.activeClients);
      const ownerId = references.employeeIdsByUsername[username];
      const prefill = readPrefill(location.search);
      const prefillClient = references.activeClients.some(
        ({ value }) => String(value) === prefill.clientId,
      );
      setForm((current) => ({
        ...current,
        ownerId: current.ownerId || (ownerId ? String(ownerId) : ''),
        clientId: prefillClient ? prefill.clientId : current.clientId,
      }));
      if (prefillClient && prefill.consultantIds.length) {
        setConsultants(
          prefill.consultantIds
            .map((id) => references.employees.find(({ value }) => value === id))
            .filter((option): option is SelectOption => Boolean(option))
            .map((option) => ({
              accountId: Number(option.value),
              fullName: option.name,
              awaitingFirstSignIn: references.activeEmployees.find(
                ({ value }) => value === option.value,
              )?.awaitingFirstSignIn,
            })),
        );
      }
      if (references.employeesOk && references.clientsOk) {
        setReferenceState('ready');
      } else if (
        references.employees.length ||
        references.activeClients.length
      ) {
        setReferenceState('partial');
      } else {
        setReferenceState('error');
      }
    });
    return () => {
      mounted = false;
      requestRevision.current += 1;
      clientRevision.current += 1;
    };
    // Reference data and the prefill are read once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the client's consultants once per client so added rows show their
  // email and contract type straight away.
  useEffect(() => {
    if (!form.clientId) {
      setClientConsultants(null);
      return undefined;
    }
    let active = true;
    fetchBulkOrderConsultants(Number(form.clientId)).then((response) => {
      if (active) {
        setClientConsultants(
          response.ok && Array.isArray(response.data) ? response.data : null,
        );
      }
    });
    return () => {
      active = false;
    };
  }, [form.clientId]);

  useEffect(() => {
    if (!clientConsultants?.length) {
      return;
    }
    setConsultants((current) =>
      current.map((row) => {
        const known = clientConsultants.find(
          ({ accountId }) => accountId === row.accountId,
        );
        return known && !row.email
          ? { ...toRow(known), values: row.values }
          : row;
      }),
    );
  }, [clientConsultants]);

  const invalidatePreview = () => {
    requestRevision.current += 1;
    setBusy(false);
    setPreview(null);
    setResult(null);
    setError(null);
    setErrorDetail('');
  };

  const updateForm = (key: keyof FormState, value: string) => {
    invalidatePreview();
    setForm((current) => ({ ...current, [key]: value }));
  };

  // The decision deadline follows the start until the issuer edits it.
  const deadlineTouched = useRef(false);
  // Rows whose own deadline the issuer edited; the others follow their start.
  const rowDeadlinesTouched = useRef(new Set<number>());
  const updateDate = (key: typeof DATE_KEYS[number], value: string) => {
    if (key === 'decisionDeadline') {
      deadlineTouched.current = true;
    }
    invalidatePreview();
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'startDate' && !deadlineTouched.current
        ? { decisionDeadline: followStartDate(value, current.decisionDeadline) }
        : {}),
    }));
  };

  const changeClient = (value: string) => {
    // One batch covers one client: consultants picked for another client
    // no longer apply.
    clientRevision.current += 1;
    setLoadingConsultants(false);
    setConsultants([]);
    rowDeadlinesTouched.current.clear();
    setEditedRow(null);
    setPickerValue(null);
    updateForm('clientId', value);
  };

  // Row corrections override the shared values for one consultant only.
  const updateRowValue = (
    accountId: number,
    key: keyof BulkOrderRowValues,
    value: string,
  ) => {
    invalidatePreview();
    if (key === 'decisionDeadline') {
      rowDeadlinesTouched.current.add(accountId);
    }
    const deadlineTouched = rowDeadlinesTouched.current.has(accountId);
    setConsultants((current) =>
      current.map((row) =>
        row.accountId === accountId
          ? {
              ...row,
              values:
                key === 'startDate'
                  ? withRowStartDate(row.values, value, deadlineTouched)
                  : { ...row.values, [key]: value },
            }
          : row,
      ),
    );
  };

  const resetRowValues = (accountId: number) => {
    invalidatePreview();
    rowDeadlinesTouched.current.delete(accountId);
    setConsultants((current) =>
      current.map((row) =>
        row.accountId === accountId ? { ...row, values: undefined } : row,
      ),
    );
  };

  const addConsultants = (additions: BulkConsultantRow[]) => {
    invalidatePreview();
    setConsultants((current) => addBulkConsultants(current, additions));
  };

  const addPickedConsultant = (option: SelectOption | null) => {
    if (option) {
      const known = clientConsultants?.find(
        ({ accountId }) => accountId === Number(option.value),
      );
      addConsultants([
        known
          ? toRow(known)
          : {
              accountId: Number(option.value),
              fullName: option.name,
              awaitingFirstSignIn: option.awaitingFirstSignIn,
            },
      ]);
    }
    setPickerValue(null);
  };

  const addAllConsultants = async () => {
    if (!form.clientId) {
      return;
    }
    const revision = clientRevision.current + 1;
    clientRevision.current = revision;
    setLoadingConsultants(true);
    setError(null);
    const response = await fetchBulkOrderConsultants(Number(form.clientId));
    if (revision !== clientRevision.current) {
      return;
    }
    setLoadingConsultants(false);
    if (response.ok && Array.isArray(response.data)) {
      addConsultants(response.data.map(toRow));
    } else {
      setError(response.status === 403 ? 'forbidden' : 'consultants');
    }
  };

  const removeConsultant = (accountId: number) => {
    invalidatePreview();
    setEditedRow((current) => (current === accountId ? null : current));
    rowDeadlinesTouched.current.delete(accountId);
    setConsultants((current) =>
      current.filter((row) => row.accountId !== accountId),
    );
  };

  const sharedFields = (): BulkOrderSharedFields | null => {
    if (
      !form.clientId ||
      !form.ownerId ||
      !form.startDate ||
      !form.endDate ||
      !form.decisionDeadline ||
      validateOrderCode(form.orderCode)
    ) {
      return null;
    }
    return {
      clientId: Number(form.clientId),
      ownerId: Number(form.ownerId),
      startDate: form.startDate,
      endDate: form.endDate,
      decisionDeadline: form.decisionDeadline,
      subject: optionalText(form.subject),
      conditions: optionalText(form.conditions),
      orderCode: toOrderCode(form.orderCode),
      notifyAccountIds: notifyAlso.map(({ value }) => Number(value)),
    };
  };

  const handleApiFailure = (status: number, message?: string) => {
    setError(status === 403 ? 'forbidden' : 'request');
    setErrorDetail(status === 400 && message ? message : '');
  };

  // The consultant list plus per-row corrections, when there are any.
  const listRequest = (shared: BulkOrderSharedFields) => {
    const rows = toBulkOrderRows(consultants);
    return {
      ...shared,
      consultantIds: consultants.map(({ accountId }) => accountId),
      ...(rows ? { rows } : {}),
    };
  };

  const runPreview = async () => {
    const dates = [
      ...DATE_KEYS.map((key) => form[key]),
      ...consultants.flatMap(({ values }) =>
        DATE_KEYS.map((key) => values?.[key]),
      ),
    ];
    if (dates.some(isInvalidDate)) {
      setError('dates');
      return;
    }
    const shared = sharedFields();
    if (!shared || (mode === 'list' ? !consultants.length : !file)) {
      setError('validation');
      return;
    }
    const conditionsMessage = validateBulkConditions(
      form.conditions,
      mode === 'list' ? consultants : [],
    );
    if (conditionsMessage) {
      setConditionsError(conditionsMessage);
      setError('conditions');
      return;
    }
    const revision = requestRevision.current + 1;
    requestRevision.current = revision;
    setBusy(true);
    setError(null);
    setErrorDetail('');
    setResult(null);
    const response =
      mode === 'list'
        ? await previewBulkOrders(listRequest(shared))
        : await previewBulkOrdersCsv(file!, shared);
    if (revision !== requestRevision.current) {
      return;
    }
    setBusy(false);
    if (response.ok && response.data) {
      setPreview(response.data);
    } else {
      handleApiFailure(response.status, response.message);
    }
  };

  const submit = async () => {
    const shared = sharedFields();
    if (!shared || !preview?.valid) {
      return;
    }
    const revision = requestRevision.current + 1;
    requestRevision.current = revision;
    setBusy(true);
    setError(null);
    setErrorDetail('');
    const response =
      mode === 'list'
        ? await issueBulkOrders(listRequest(shared))
        : await issueBulkOrdersCsv(file!, shared);
    if (revision !== requestRevision.current) {
      return;
    }
    setBusy(false);
    if (
      (response.ok || response.status === 400) &&
      isBulkOrderResult(response.data)
    ) {
      setResult(response.data);
      if (!response.data.created) {
        setPreview({ ...preview, valid: false, errors: response.data.errors });
      }
    } else if (!response.ok) {
      handleApiFailure(response.status, response.message);
    } else {
      handleApiFailure(0);
    }
  };

  const fileChanged = (event: ChangeEvent<HTMLInputElement>) => {
    invalidatePreview();
    setFile(event.target.files?.[0] || null);
  };

  const availableConsultants = activeEmployees.filter(
    ({ value }) => !consultants.some((row) => row.accountId === value),
  );
  const previewHasPeriods = Boolean(
    preview?.rows.some(({ startDate }) => Boolean(startDate)),
  );
  const rowPeriod = (row: BulkConsultantRow) =>
    `${formatDate(row.values?.startDate || form.startDate)} – ${formatDate(
      row.values?.endDate || form.endDate,
    )}`;

  return (
    <Styles.Page>
      <PageHeader
        title={intl.formatMessage({ id: 'ORDERS.BULK.ISSUE_TITLE' })}
        description={intl.formatMessage({
          id: 'ORDERS.BULK.ISSUE_DESCRIPTION',
        })}
        actions={
          <Button component={Link} to="/assignments">
            {intl.formatMessage({ id: 'ORDERS.BACK' })}
          </Button>
        }
      />

      {referenceState === 'loading' && <CircularProgress />}
      {referenceState === 'partial' && (
        <Alert severity="warning">
          {intl.formatMessage({ id: 'ORDERS.BULK.REFERENCE_PARTIAL' })}
        </Alert>
      )}
      {referenceState === 'error' && (
        <Alert severity="error">
          {intl.formatMessage({ id: 'ORDERS.REFERENCE_ERROR' })}
        </Alert>
      )}
      {error && (
        <Alert severity={error === 'validation' ? 'warning' : 'error'}>
          {intl.formatMessage({
            id: bulkErrorMessage(error, conditionsError),
          })}
          {errorDetail && <div>{errorDetail}</div>}
        </Alert>
      )}

      <Styles.FormPaper>
        <SearchableSelect
          id="bulk-order-client"
          required
          fullWidth
          size="medium"
          label={intl.formatMessage({ id: 'ORDERS.CLIENT' })}
          options={clients.map(({ value, name }) => ({ value, label: name }))}
          value={form.clientId}
          onChange={changeClient}
        />

        <Box mt={2}>
          <Tabs
            value={mode}
            onChange={(_event, value: InputMode) => {
              invalidatePreview();
              setMode(value);
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="list"
              label={intl.formatMessage({ id: 'ORDERS.BULK.CONSULTANTS' })}
            />
            <Tab
              value="csv"
              label={intl.formatMessage({ id: 'ORDERS.BULK.CSV' })}
            />
          </Tabs>
        </Box>

        {mode === 'list' ? (
          <Box my={2}>
            <Box display="flex" gridGap={12} alignItems="flex-start">
              <Box flex={1}>
                <Autocomplete
                  id="bulk-order-consultant-picker"
                  options={availableConsultants}
                  getOptionLabel={(option) =>
                    consultantName(
                      intl,
                      option.name,
                      option.awaitingFirstSignIn,
                    )
                  }
                  getOptionSelected={(option, value) =>
                    option.value === value.value
                  }
                  value={pickerValue}
                  inputValue={pickerInput}
                  onInputChange={(_event, value, reason) =>
                    setPickerInput(reason === 'reset' ? '' : value)
                  }
                  onChange={(_event, option) => addPickedConsultant(option)}
                  disabled={!clientSelected}
                  noOptionsText={intl.formatMessage({
                    id: 'ORDERS.BULK.NO_CONSULTANTS',
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={intl.formatMessage({
                        id: 'ORDERS.BULK.ADD_CONSULTANT',
                      })}
                      helperText={
                        clientSelected
                          ? undefined
                          : intl.formatMessage({
                              id: 'ORDERS.BULK.SELECT_CLIENT_FIRST',
                            })
                      }
                    />
                  )}
                />
              </Box>
              <Button
                color="primary"
                variant="outlined"
                onClick={addAllConsultants}
                disabled={
                  !clientSelected || loadingConsultants || addAllCount === 0
                }
                style={{ marginTop: 8 }}
              >
                {loadingConsultants ? (
                  <CircularProgress size={22} />
                ) : (
                  intl.formatMessage(
                    {
                      id: addAllCount
                        ? 'ORDERS.BULK.ADD_ALL_COUNT'
                        : 'ORDERS.BULK.ADD_ALL',
                    },
                    { count: addAllCount },
                  )
                )}
              </Button>
            </Box>
            {consultants.length > 0 ? (
              <Table
                size="small"
                aria-label={intl.formatMessage({
                  id: 'ORDERS.BULK.CONSULTANTS',
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.BULK.ROW' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.CONSULTANT_EMAIL' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.CONTRACT_TYPE' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.PERIOD' })}
                    </TableCell>
                    <TableCell align="right">
                      {intl.formatMessage({ id: 'ORDERS.ACTIONS' })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultants.map((row, index) => {
                    const name = consultantName(
                      intl,
                      row.fullName,
                      row.awaitingFirstSignIn,
                    );
                    const exclude = intl.formatMessage(
                      { id: 'ORDERS.BULK.EXCLUDE' },
                      { name },
                    );
                    const edit = intl.formatMessage(
                      { id: 'ORDERS.BULK.EDIT_ROW' },
                      { name },
                    );
                    const editing = editedRow === row.accountId;
                    const corrected = Boolean(toBulkOrderRows([row]));
                    return (
                      <Fragment key={row.accountId}>
                        <TableRow selected={editing}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{name}</TableCell>
                          <TableCell>{row.email || '—'}</TableCell>
                          <TableCell>
                            {row.contractType
                              ? intl.formatMessage({
                                  id: `ORDERS.CONTRACT.${row.contractType}`,
                                  defaultMessage: row.contractType,
                                })
                              : '—'}
                          </TableCell>
                          <Styles.NoWrapCell>
                            {rowPeriod(row)}
                            {corrected && (
                              <Styles.CellNote>
                                {intl.formatMessage({
                                  id: 'ORDERS.BULK.ROW_CORRECTED',
                                })}
                              </Styles.CellNote>
                            )}
                          </Styles.NoWrapCell>
                          <Styles.NoWrapCell align="right">
                            <Tooltip title={edit}>
                              <IconButton
                                size="small"
                                aria-label={edit}
                                aria-expanded={editing}
                                color={editing ? 'primary' : 'default'}
                                onClick={() =>
                                  setEditedRow(editing ? null : row.accountId)
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={exclude}>
                              <IconButton
                                size="small"
                                aria-label={exclude}
                                onClick={() => removeConsultant(row.accountId)}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Styles.NoWrapCell>
                        </TableRow>
                        {editing && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Styles.RowEditor>
                                {DATE_KEYS.map((key) => (
                                  <DateField
                                    key={key}
                                    id={`bulk-row-${row.accountId}-${key}`}
                                    size="small"
                                    disablePast={key === 'decisionDeadline'}
                                    min={
                                      key === 'decisionDeadline'
                                        ? undefined
                                        : earliestStart
                                    }
                                    max={
                                      key === 'decisionDeadline'
                                        ? getDeadlineMax(
                                            row.values?.startDate ||
                                              form.startDate,
                                          )
                                        : undefined
                                    }
                                    label={intl.formatMessage({
                                      id: DATE_LABELS[key],
                                    })}
                                    value={row.values?.[key] || ''}
                                    helperText={intl.formatMessage(
                                      { id: 'ORDERS.BULK.SHARED_VALUE' },
                                      { value: formatDate(form[key]) },
                                    )}
                                    onChange={(value) =>
                                      updateRowValue(row.accountId, key, value)
                                    }
                                  />
                                ))}
                                <Styles.FullWidth>
                                  <OrderSubjectField
                                    id={`bulk-row-${row.accountId}-subject`}
                                    size="small"
                                    label="ORDERS.SUBJECT"
                                    help="ORDERS.BULK.ROW_SUBJECT_HELP"
                                    value={row.values?.subject || ''}
                                    onChange={(value) =>
                                      updateRowValue(
                                        row.accountId,
                                        'subject',
                                        value,
                                      )
                                    }
                                  />
                                </Styles.FullWidth>
                                <Styles.FullWidth>
                                  <TextField
                                    id={`bulk-row-${row.accountId}-conditions`}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ maxLength: 10000 }}
                                    label={intl.formatMessage({
                                      id: 'ORDERS.CONDITIONS',
                                    })}
                                    helperText={intl.formatMessage({
                                      id: 'ORDERS.BULK.ROW_CONDITIONS_HELP',
                                    })}
                                    value={row.values?.conditions || ''}
                                    onChange={(event) =>
                                      updateRowValue(
                                        row.accountId,
                                        'conditions',
                                        event.target.value,
                                      )
                                    }
                                  />
                                </Styles.FullWidth>
                                <Styles.FullWidth>
                                  <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    gridGap={8}
                                  >
                                    <Button
                                      size="small"
                                      disabled={!corrected}
                                      onClick={() =>
                                        resetRowValues(row.accountId)
                                      }
                                    >
                                      {intl.formatMessage({
                                        id: 'ORDERS.BULK.ROW_RESET',
                                      })}
                                    </Button>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() => setEditedRow(null)}
                                    >
                                      {intl.formatMessage({
                                        id: 'ORDERS.BULK.ROW_DONE',
                                      })}
                                    </Button>
                                  </Box>
                                </Styles.FullWidth>
                              </Styles.RowEditor>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage(
                    { id: getEmptyBatchMessage(clientSelected, addAllCount) },
                    { count: addAllCount, client: clientName },
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box my={2}>
            <input
              aria-label={intl.formatMessage({ id: 'ORDERS.BULK.CSV' })}
              type="file"
              accept=".csv,text/csv"
              onChange={fileChanged}
            />
            <Typography variant="caption" display="block">
              {intl.formatMessage({ id: 'ORDERS.BULK.CSV_HELP' })}
            </Typography>
          </Box>
        )}

        <Styles.FormGrid>
          <SearchableSelect
            id="bulk-order-owner"
            required
            size="medium"
            label={intl.formatMessage({ id: 'ORDERS.OWNER' })}
            options={employees.map(({ value, name }) => ({
              value,
              label: name,
            }))}
            value={form.ownerId}
            onChange={(value) => updateForm('ownerId', value)}
          />
          {DATE_KEYS.map((key) => (
            <DateField
              key={key}
              id={`bulk-order-${key}`}
              required
              disablePast={key === 'decisionDeadline'}
              min={key === 'decisionDeadline' ? undefined : earliestStart}
              max={
                key === 'decisionDeadline'
                  ? getDeadlineMax(form.startDate)
                  : undefined
              }
              label={intl.formatMessage({ id: DATE_LABELS[key] })}
              value={form[key]}
              onChange={(value) => updateDate(key, value)}
            />
          ))}
          <OrderCodeField
            id="bulk-order-code"
            value={form.orderCode}
            onChange={(value) => updateForm('orderCode', value)}
          />
          <Styles.FullWidth>
            <Autocomplete
              id="bulk-order-notify-also"
              multiple
              options={notifyCandidates}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              value={notifyAlso}
              onChange={(_event, value) => {
                invalidatePreview();
                setNotifyAlso(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={intl.formatMessage({ id: 'ORDERS.NOTIFY_ALSO' })}
                  helperText={intl.formatMessage({
                    id: 'ORDERS.NOTIFY_ALSO_HELP',
                  })}
                />
              )}
            />
          </Styles.FullWidth>
          <Styles.FullWidth>
            <OrderSubjectField
              id="bulk-order-subject"
              value={form.subject}
              onChange={(value) => updateForm('subject', value)}
            />
          </Styles.FullWidth>
          <Styles.FullWidth>
            <TextField
              id="bulk-order-conditions"
              fullWidth
              multiline
              minRows={6}
              variant="outlined"
              required
              inputProps={{ maxLength: 10000 }}
              label={intl.formatMessage({ id: 'ORDERS.CONDITIONS' })}
              error={error === 'conditions'}
              helperText={intl.formatMessage({
                id: 'ORDERS.CONDITIONS_HELP',
              })}
              value={form.conditions}
              onChange={(event) => updateForm('conditions', event.target.value)}
            />
          </Styles.FullWidth>
        </Styles.FormGrid>

        <Box display="flex" justifyContent="flex-end">
          <Button
            color="primary"
            variant={preview?.valid ? 'outlined' : 'contained'}
            onClick={runPreview}
            disabled={busy || referenceState === 'loading'}
          >
            {busy && !preview ? (
              <CircularProgress size={22} />
            ) : (
              intl.formatMessage({ id: 'ORDERS.BULK.CHECK_LIST' })
            )}
          </Button>
        </Box>

        {preview && (
          <Box mt={3}>
            <Alert severity={preview.valid ? 'success' : 'error'}>
              {intl.formatMessage(
                {
                  id: preview.valid
                    ? 'ORDERS.BULK.PREVIEW_VALID'
                    : 'ORDERS.BULK.PREVIEW_BLOCKED',
                },
                { rows: preview.rows.length, errors: preview.errors.length },
              )}
            </Alert>
            {preview.rows.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.BULK.ROW' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.CONSULTANT_EMAIL' })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
                    </TableCell>
                    {previewHasPeriods && (
                      <>
                        <TableCell>
                          {intl.formatMessage({ id: 'ORDERS.PERIOD' })}
                        </TableCell>
                        <TableCell>
                          {intl.formatMessage({ id: 'ORDERS.DEADLINE' })}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow key={`${row.row}-${row.email}`}>
                      <TableCell>{row.row}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.consultantName}</TableCell>
                      {previewHasPeriods && (
                        <>
                          <Styles.NoWrapCell>
                            {formatDate(row.startDate)} –{' '}
                            {formatDate(row.endDate)}
                          </Styles.NoWrapCell>
                          <Styles.NoWrapCell>
                            {formatDate(row.decisionDeadline)}
                          </Styles.NoWrapCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {preview.errors.length > 0 && (
              <>
                <Box display="flex" justifyContent="flex-end" my={1}>
                  <Button
                    onClick={() =>
                      saveTextFile(
                        bulkErrorsCsv(preview.errors),
                        'assignment-bulk-errors.csv',
                      )
                    }
                  >
                    {intl.formatMessage({ id: 'ORDERS.BULK.DOWNLOAD_ERRORS' })}
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['ROW', 'EMAIL', 'CODE', 'MESSAGE'].map((column) => (
                        <TableCell key={column}>
                          {intl.formatMessage({ id: `ORDERS.BULK.${column}` })}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.errors.map((item, index) => (
                      <TableRow key={`${item.row}-${item.code}-${index}`}>
                        <TableCell>{item.row || '—'}</TableCell>
                        <TableCell>{item.email || '—'}</TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            {preview.valid && !result?.created && (
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gridGap={16}
                flexWrap="wrap"
              >
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage({ id: 'ORDERS.BULK.ATOMIC_WARNING' })}
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={busy}
                  onClick={submit}
                >
                  {busy ? (
                    <CircularProgress size={22} />
                  ) : (
                    intl.formatMessage(
                      { id: 'ORDERS.BULK.ISSUE_COUNT' },
                      { count: preview.rows.length },
                    )
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {result?.created && (
          <Box mt={3}>
            <Alert severity="success">
              {intl.formatMessage(
                { id: 'ORDERS.BULK.SUCCESS' },
                { count: result.orders.length },
              )}
            </Alert>
            <ul>
              {result.orders.map(({ order }) => (
                <li key={order.orderNumber}>
                  <Link
                    to={`/assignments/${encodeURIComponent(order.orderNumber)}`}
                  >
                    {order.orderNumber}
                  </Link>{' '}
                  — {order.consultantName}
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Styles.FormPaper>
    </Styles.Page>
  );
};

const BulkOrderIssue = () => (
  <OrderAccess requireManage>{() => <BulkOrderIssueContent />}</OrderAccess>
);

export default BulkOrderIssue;
